import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../../contexts/AuthContext';
import { LockClosedIcon } from '@heroicons/react/24/solid';

const AdminLoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation schema
  const AdminLoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters')
  });

  // Initial form values
  const initialValues = {
    email: '',
    password: ''
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setLoginError('');

    try {
      const response = await login({
        email: values.email,
        password: values.password,
        isAdmin: true // Flag to indicate admin login attempt
      });

      if (response.success) {
        // Redirect to admin dashboard
        navigate('/admin', { replace: true });
      } else {
        // Display the specific error message from the response
        setLoginError(response.error || 'Login failed. Please check your credentials or access permissions.');
      }
    } catch (error) {
      // Provide more specific error message if available
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      setLoginError(errorMessage);
      console.error('Admin login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Admin Portal
          </h2>
          <h3 className="mt-2 text-center text-xl text-gray-300">
            Teem Wildlife Wonders
          </h3>
          <p className="mt-2 text-center text-sm text-gray-400">
            Sign in to access the administration dashboard
          </p>
        </div>
        
        <div className="mt-8 bg-gray-800 py-8 px-4 shadow rounded-lg sm:px-10">
          {loginError && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{loginError}</span>
            </div>
          )}
          
          <Formik
            initialValues={initialValues}
            validationSchema={AdminLoginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    Email address
                  </label>
                  <div className="mt-1">
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-gray-700 text-white border-gray-600 ${
                        errors.email && touched.email ? 'border-red-500' : ''
                      }`}
                      placeholder="admin@example.com"
                    />
                    <ErrorMessage name="email" component="p" className="mt-2 text-sm text-red-500" />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <div className="mt-1">
                    <Field
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm bg-gray-700 text-white border-gray-600 ${
                        errors.password && touched.password ? 'border-red-500' : ''
                      }`}
                      placeholder="••••••••"
                    />
                    <ErrorMessage name="password" component="p" className="mt-2 text-sm text-red-500" />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <LockClosedIcon className="h-5 w-5 text-primary-dark group-hover:text-primary" aria-hidden="true" />
                    </span>
                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">
                  Admin access only
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Teem Wildlife Wonders. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
